import { useEffect, useRef } from 'react'
import { useToast } from '../contexts/ToastContext'
import useScoutOperationStore from '../stores/scoutOperationStore'
import useScoutStore from '../stores/scoutStore'
import { scoutAccountService } from '../services/scoutAccountService'

const POLL_INTERVAL_MS = 15_000
const TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes

const DONE_STATUSES = new Set(['completed', 'partial'])
const FAILED_STATUSES = new Set(['failed'])

export function useScoutPolling() {
  const { showSuccess, showError, showInfo } = useToast()
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    intervalRef.current = setInterval(async () => {
      const { pendingOperations, removeOperation, markCompleted } =
        useScoutOperationStore.getState()

      if (pendingOperations.length === 0) return

      for (const op of pendingOperations) {
        // Timeout check
        if (Date.now() - op.startedAt > TIMEOUT_MS) {
          removeOperation(op.scanId)
          useScoutStore.getState().loadAccounts(op.customerId)
          showInfo(
            'Scout Scan Timeout',
            `Scan for @${op.username} timed out. It may still complete in the background.`
          )
          continue
        }

        try {
          const response = await scoutAccountService.getScanStatus(
            op.scanId,
            op.customerId
          )

          if (!response.success || !response.data) continue

          const { status, brands_detected } = response.data

          if (DONE_STATUSES.has(status)) {
            markCompleted(op.scanId)
            useScoutStore.getState().loadAccounts(op.customerId)
            showSuccess(
              'Scout Scan Complete',
              `@${op.username} scan finished. ${brands_detected} brand${brands_detected !== 1 ? 's' : ''} detected.`
            )
          } else if (FAILED_STATUSES.has(status)) {
            removeOperation(op.scanId)
            useScoutStore.getState().loadAccounts(op.customerId)
            showError(
              'Scout Scan Failed',
              `Scan for @${op.username} failed.`
            )
          }
          // Other statuses (pending, scraping, analyzing, scoring) — keep polling
        } catch {
          // Network error — keep polling, don't remove
        }
      }
    }, POLL_INTERVAL_MS)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [showSuccess, showError, showInfo])
}
